<template>
  <div v-if="!meterDetail && isLoading" class="tw-flex tw-gap-3 tw-items-center tw-p-8 tw-text-gray-400">
    <div class="tw-h-8 tw-w-8 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-blue-500" />
    Laden...
  </div>
  <div v-else-if="error || meterDetail?.status === 'error'" class="tw-p-8 tw-text-red-500">
    Meter niet gevonden of fout opgetreden.
  </div>
  <div
    v-else-if="meterDetail"
    class="tw-flex tw-flex-col tw-p-4 tw-gap-4 tw-transition-opacity"
    :class="{ 'tw-opacity-50 tw-pointer-events-none': isPreviousData }"
  >
    <!-- Waarschuwing: te weinig productie, dus niet in Energize en niet in de app -->
    <energize-sync-warning :meter-detail="meterDetail" />

    <!-- The address and one of its meters: address on top, the bar with all its meters (a CVZ's fake addresses
         too), then tenant/hardware, the selected meter and its vendor details; switching meters only changes the meter -->
    <meter-panel
      :serial-number="selectedMeter"
      :device="selectedDevice"
      :address-kind="addressKind"
    >
      <template #header>
        <address-header
          :meter-detail="meterDetail"
          :kind="addressKind"
          :warnings="addressWarnings"
          :projects="addressProjects"
        />
      </template>
      <template #tabs>
        <meter-tabs
          :devices="addressDevices"
          :selected="selectedMeter"
          :is-loading="isLoadingAddressDevices"
          @select="selectMeter"
        />
      </template>
      <template #address="{ hardwareMeter }">
        <tenant-card :meter-detail="meterDetail" />
        <hardware-card :meter-detail="meterDetail" :meter="hardwareMeter" />
      </template>
    </meter-panel>

    <!-- Grafiek: the whole address by default (summed or per meter), or one meter -->
    <div
      class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-p-4 tw-flex tw-flex-col tw-gap-2"
      :style="{ height: addressDevices.length > 1 ? '460px' : '420px' }"
    >
      <chart-scope-switcher
        v-if="addressDevices.length > 1"
        v-model="chartSelection"
        :split.sync="chartSplit"
        :devices="addressDevices"
        :kind="addressKind"
      />
      <div class="tw-flex-1 tw-min-h-0">
      <bar-chart
        ref="barChartRef"
        :serial-number="chartSerial"
        :scope="chartScope"
        :split-meters="chartSplitMeters"
        :expected-production="chartExpectedProduction"
        :neighbor-meters="neighborMeters"
        :neighbor-match-type="neighborMatchType"
        :num-of-panels="chartPanels.count"
        :panel-wp="chartPanels.wp"
        :congestion-intervals="congestionIntervals"
        @view-date-change="voltageDate = $event"
      />
      </div>
    </div>

    <!-- Alarmen of the selected meter -->
    <alarm-status :serial-number="selectedMeter" :meter-detail="selectedMeterDetail" />

    <!-- Vergelijking buren -->
    <div v-if="barChartRef?.showNeighbors && barChartRef?.activeNeighbors?.length > 0" class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-flex-shrink-0">
      <button
        class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-50 tw-rounded-t-xl tw-transition-colors"
        @click="neighborInfoExpanded = !neighborInfoExpanded"
      >
        <svg class="tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform" :class="neighborInfoExpanded ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        <h3 class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-tracking-wider tw-uppercase">
          {{ neighborMatchType === 'postcode' ? 'Vergelijking postcodegebied' : 'Vergelijking buren' }}
        </h3>
        <span class="tw-text-xs tw-bg-gray-100 tw-text-gray-500 tw-rounded-full tw-px-2 tw-py-0.5 tw-font-medium">
          {{ barChartRef.activeNeighbors.length }}
        </span>
      </button>
      <div v-if="neighborInfoExpanded" class="tw-px-4 tw-pb-3">
        <!-- Details table -->
        <table class="tw-w-full tw-text-xs tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
          <thead>
            <tr class="tw-bg-gray-50 tw-text-gray-500 tw-text-left">
              <th class="tw-px-2 tw-py-1.5"></th>
              <th class="tw-px-2 tw-py-1.5">Adres</th>
              <th class="tw-px-2 tw-py-1.5">Panelen</th>
              <th class="tw-px-2 tw-py-1.5">Wp</th>
              <th class="tw-px-2 tw-py-1.5">Oriëntatie</th>
              <th class="tw-px-2 tw-py-1.5">Helling</th>
            </tr>
          </thead>
          <tbody>
            <!-- Main meter (highlighted) -->
            <tr class="tw-border-t tw-border-gray-200 tw-bg-blue-50 tw-font-medium">
              <td class="tw-px-2 tw-py-1.5">
                <span class="tw-inline-block tw-w-2.5 tw-h-2.5 tw-rounded-sm tw-bg-green-500" />
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-800">
                {{ meterDetail.street }} {{ meterDetail.house_number }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-800">
                {{ chartPanels.count ?? '—' }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-800">
                {{ chartPanels.totalWp ? `${chartPanels.totalWp} Wp` : '—' }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-800">
                {{ meterDetail.orientation != null ? `${meterDetail.orientation}° ${azimuthToLabel(meterDetail.orientation)}` : '—' }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-800">
                {{ meterDetail.tilt != null ? `${meterDetail.tilt}°` : '—' }}
              </td>
            </tr>
            <!-- Neighbors -->
            <tr
              v-for="(meter, idx) in barChartRef.activeNeighbors"
              :key="meter.serialNumber"
              class="tw-border-t tw-border-gray-50"
            >
              <td class="tw-px-2 tw-py-1.5">
                <span
                  class="tw-inline-block tw-w-2.5 tw-h-2.5 tw-rounded-full"
                  :style="{ backgroundColor: barChartRef.NEIGHBOR_COLORS[idx % barChartRef.NEIGHBOR_COLORS.length] }"
                />
              </td>
              <td class="tw-px-2 tw-py-1.5">
                <router-link
                  :to="{ name: 'polling-detail', params: { id: meter.serialNumber } }"
                  class="tw-text-blue-600 hover:tw-underline tw-no-underline"
                >{{ meter.address }}</router-link>
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-600">
                {{ barChartRef.neighborDetailMap[meter.serialNumber]?.num_of_panels ?? '—' }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-600">
                {{ barChartRef.neighborWpMap[meter.serialNumber] ? `${barChartRef.neighborWpMap[meter.serialNumber]} Wp` : '—' }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-600">
                {{ barChartRef.neighborDetailMap[meter.serialNumber]?.orientation != null ? `${barChartRef.neighborDetailMap[meter.serialNumber].orientation}° ${azimuthToLabel(barChartRef.neighborDetailMap[meter.serialNumber].orientation)}` : '—' }}
              </td>
              <td class="tw-px-2 tw-py-1.5 tw-text-gray-600">
                {{ barChartRef.neighborDetailMap[meter.serialNumber]?.tilt != null ? `${barChartRef.neighborDetailMap[meter.serialNumber].tilt}°` : '—' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- 10-daagse analyse -->
    <multi-day-analysis
      :serial-number="chartSerial"
      :scope="chartScope"
      :neighbor-meters="neighborMeters"
      :neighbor-match-type="neighborMatchType"
      :main-wp="chartPanels.totalWp"
      :neighbor-wp-map="barChartRef?.neighborWpMap || {}"
      :expected-production="chartExpectedProduction"
      @navigate-to-day="navigateToDay"
    />

    <!-- Jaaranalyse (12 maanden) -->
    <yearly-analysis
      :serial-number="chartSerial"
      :scope="chartScope"
      :neighbor-meters="neighborMeters"
      :neighbor-match-type="neighborMatchType"
      :main-wp="chartPanels.totalWp"
      :neighbor-wp-map="barChartRef?.neighborWpMap || {}"
      :expected-production="chartExpectedProduction"
    />

    <!-- Meerjarenanalyse (5 jaar) -->
    <five-year-analysis
      :serial-number="chartSerial"
      :scope="chartScope"
      :main-wp="chartPanels.totalWp"
      :expected-production="chartExpectedProduction"
      :date-activated="meterDetail.date_activated"
    />

    <!-- Netspanning (voltage) -->
    <div class="tw-bg-white tw-rounded-xl tw-shadow-sm tw-flex-shrink-0">
      <button
        class="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-text-left hover:tw-bg-gray-50 tw-rounded-t-xl tw-transition-colors"
        @click="voltageExpanded = !voltageExpanded"
      >
        <svg class="tw-w-4 tw-h-4 tw-text-gray-400 tw-transition-transform" :class="voltageExpanded ? 'tw-rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
        <h3 class="tw-font-semibold tw-text-xs tw-text-gray-500 tw-tracking-wider tw-uppercase">
          Netspanning
        </h3>
      </button>
      <voltage-info
        v-show="voltageExpanded"
        :serial-number="serialNumber"
        :selected-date="voltageDate"
        :neighbor-meters="neighborMeters"
        @update:congestionIntervals="congestionIntervals = $event"
      />
    </div>

    <!-- Activiteitenlog -->
    <address-logs :meter-detail="meterDetail" />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useQuery } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { MAX_NEIGHBORS } from './constants'
import { useAddressDevices } from './composables/useAddressDevices'
import { useMeterDetail } from './composables/useMeterDetail'
import { useAddressLocation } from './composables/useAddressLocation'
import { azimuthToLabel } from './solar'
import { addressLabelNL } from './formatters'
import EnergizeSyncWarning from './EnergizeSyncWarning.vue'
import TenantCard from './TenantCard.vue'
import AddressHeader from './AddressHeader.vue'
import MeterPanel from './MeterPanel.vue'
import MeterTabs from './MeterTabs.vue'
import ChartScopeSwitcher from './ChartScopeSwitcher.vue'
import HardwareCard from './HardwareCard.vue'
import BarChart from './BarChart.vue'
import AddressLogs from './AddressLogs.vue'
import AlarmStatus from './AlarmStatus.vue'
import VoltageInfo from './VoltageInfo.vue'
import MultiDayAnalysis from './MultiDayAnalysis.vue'
import YearlyAnalysis from './YearlyAnalysis.vue'
import FiveYearAnalysis from './FiveYearAnalysis.vue'

const props = defineProps({
  metersForComparison: { type: Array, required: false, default: () => [] },
})

const route = useRoute()
const router = useRouter()
// the meter the page was opened on: it fixes the address
const serialNumber = computed(() => route.params?.id || '')
const voltageExpanded = ref(false)
const neighborInfoExpanded = ref(false)
const barChartRef = ref(null)

const navigateToDay = (dateStr) => {
  if (barChartRef.value?.navigateToDay) {
    barChartRef.value.navigateToDay(dateStr)
  }
}
const voltageDate = ref(new Date())
const congestionIntervals = ref([])

// Meter detail ophalen
// keepPreviousData: when switching, the previous meter stays visible (dimmed, isPreviousData) until the new one is loaded
const { data: meterDetail, isLoading, isPreviousData, error } = useMeterDetail(serialNumber, { keepPreviousData: true })


// ─── Auto-discover neighbor meters ──────────────────────────────────────────
const neighborZip = computed(() => meterDetail.value?.zip_code ?? '')

const { data: neighborSearchData } = useQuery({
  queryKey: computed(() => ['neighborSearch', neighborZip.value]),
  queryFn: async () => {
    if (!neighborZip.value) return []
    const urlParams = new URLSearchParams({ query: neighborZip.value, limit: 250 })
    const result = await fetchData(`${READINGS_API_BASE}/meters?${urlParams}`)
    return result?.data ?? []
  },
  enabled: computed(() => !!neighborZip.value),
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
})

// Strategy 1: same street + same city
const streetNeighbors = computed(() => {
  if (!meterDetail.value || !neighborSearchData.value) return []
  const targetStreet = meterDetail.value.street
  const targetCity = meterDetail.value.city
  const targetHouseNum = parseInt(meterDetail.value.house_number) || 0
  if (!targetStreet) return []
  return neighborSearchData.value
    .filter(m =>
      m.serial_number !== serialNumber.value &&
      m.street === targetStreet &&
      m.city === targetCity
    )
    .sort((a, b) => {
      const numA = parseInt(a.house_number) || 0
      const numB = parseInt(b.house_number) || 0
      // Prefer same parity (odd/even) — same side of the street in NL
      const sameSideA = (numA % 2 === targetHouseNum % 2) ? 0 : 1
      const sameSideB = (numB % 2 === targetHouseNum % 2) ? 0 : 1
      if (sameSideA !== sameSideB) return sameSideA - sameSideB
      // Then sort by proximity
      return Math.abs(numA - targetHouseNum) - Math.abs(numB - targetHouseNum)
    })
    .slice(0, MAX_NEIGHBORS)
    .map(m => ({
      serialNumber: m.serial_number,
      address: `${m.street} ${m.house_number}`,
      matchType: 'street',
    }))
})

// Strategy 2 (fallback): same postcode area, similar orientation (±30°) and tilt (±10°)
const postcodeNeighbors = computed(() => {
  if (!meterDetail.value || !neighborSearchData.value) return []
  if (streetNeighbors.value.length > 0) return [] // don't fallback if street neighbors exist
  const targetOrientation = Number(meterDetail.value.orientation)
  const targetTilt = Number(meterDetail.value.tilt)
  const hasOrientationData = !isNaN(targetOrientation)
  const hasTiltData = !isNaN(targetTilt)

  const orientationMatch = (o) => {
    if (!hasOrientationData || o == null) return true // skip if no data on either side
    const diff = Math.abs(Number(o) - targetOrientation)
    return Math.min(diff, 360 - diff) <= 30
  }
  const tiltMatch = (t) => {
    if (!hasTiltData || t == null) return true
    return Math.abs(Number(t) - targetTilt) <= 10
  }

  return neighborSearchData.value
    .filter(m =>
      m.serial_number !== serialNumber.value &&
      orientationMatch(m.orientation) &&
      tiltMatch(m.tilt)
    )
    .slice(0, MAX_NEIGHBORS)
    .map(m => ({
      serialNumber: m.serial_number,
      address: `${m.street} ${m.house_number}`,
      matchType: 'postcode',
    }))
})

const neighborMeters = computed(() =>
  streetNeighbors.value.length > 0 ? streetNeighbors.value : postcodeNeighbors.value
)

const neighborMatchType = computed(() =>
  neighborMeters.value.length > 0 ? neighborMeters.value[0].matchType : null
)

// ─── The address: its meters and inverters, and its ERP projects ────────────
const {
  devices: addressDevices,
  kind: addressKind,
  warnings: addressWarnings,
  isLoading: isLoadingAddressDevices,
} = useAddressDevices(serialNumber, meterDetail)
const { projects: addressProjects } = useAddressLocation(meterDetail)

// The meter section shows one meter of the address: ?meter=<serial>, by default the one the page was opened on.
// Only a meter of this address: an old link to a meter that moved, or a typo, falls back to the page meter
// instead of showing (and acting on) another address's meter under this address. While the address is
// loading the link is trusted, so it does not flip to the page meter and back.
const selectedMeter = computed(() => {
  const requested = route.query?.meter
  if (!requested) return serialNumber.value
  if (isLoadingAddressDevices.value) return requested
  return addressDevices.value.some((d) => d.serial_number === requested) ? requested : serialNumber.value
})
const selectMeter = (serial) => {
  if (serial === selectedMeter.value) return
  const query = { ...route.query }
  if (serial === serialNumber.value) delete query.meter
  else query.meter = serial
  router.replace({ query })
}

// its entry in the address view: first and last data, history
const selectedDevice = computed(() => addressDevices.value.find((d) => d.serial_number === selectedMeter.value) ?? null)
// The browser tab shows the address. The router sets "<route name> | ERP | NORM" after every navigation
// (also when a tab changes ?meter=), so this runs after it.
watch(
  [() => addressLabelNL(meterDetail.value, addressKind.value), () => route.fullPath],
  ([label]) => { if (label) document.title = `${label} | ERP | NORM` },
  { immediate: true, flush: 'post' }
)

// the selected meter's detail, shared with the meter section through the query cache
const { data: selectedMeterDetail } = useMeterDetail(selectedMeter)

// The charts: the whole address (scope address: every meter there, a CVZ's fake addresses too), or one meter
const chartSelection = ref('address')
watch(serialNumber, () => { chartSelection.value = 'address' })
const chartSerial = computed(() => chartSelection.value === 'address' ? serialNumber.value : chartSelection.value)
const chartScope = computed(() => chartSelection.value === 'address' ? 'address' : 'meter')
// 'Per meter': every meter of the address as its own series instead of the sum (at most 12, the API's limit)
const chartSplit = ref(false)
watch(serialNumber, () => { chartSplit.value = false })
const chartSplitMeters = computed(() => {
  if (chartSelection.value !== 'address' || !chartSplit.value || addressDevices.value.length < 2) return []
  return addressDevices.value.slice(0, 12).map((d) => ({
    serial: d.serial_number,
    label: [
      d.serial_number,
      addressKind.value === 'cvz' && d.house_number ? d.house_number : null,
      d.current ? null : 'vervangen',
    ].filter(Boolean).join(' · '),
  }))
})

// Forecast and Wp of what the chart shows. Panels, Wp and expected production are per meter, so they only
// apply when the chart covers one meter: the selected chip, or the address when it has just this meter.
// For the sum (or the split) of several meters they are left out rather than taken from the page meter.
const chartCoversOneMeter = computed(() => chartSelection.value !== 'address' || addressDevices.value.length <= 1)
const { data: chartMeterData } = useMeterDetail(chartSerial)
const chartMeterDetail = computed(() => (chartCoversOneMeter.value ? chartMeterData.value ?? null : null))
const chartPanels = computed(() => {
  const count = chartMeterDetail.value?.num_of_panels ?? null
  const wp = chartMeterDetail.value?.panel_wp ?? null
  return { count, wp, totalWp: count && wp ? count * wp : null }
})
// the meter's expected production, else panels × Wp × 900 kWh/kWp (NL average), in Wh/year
const chartExpectedProduction = computed(() =>
  chartMeterDetail.value?.expected_production || (chartPanels.value.totalWp ? chartPanels.value.totalWp * 900 : null)
)

</script>
